import {
  Box,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Badge,
  Chip,
  Button as MuiButton,
} from "@mui/material";
import React, { useEffect, useState, useRef, useCallback } from "react";
import { Button, Heading, MenuButton } from "../../components/Common";
import MongoSyncModal from "../../components/Common/MongoSyncModal";
import { getGlobalSettings } from "../../Service/sync.service";
import { VscFilter } from "react-icons/vsc";
import { IoSyncOutline } from "react-icons/io5";
import { Pagination, Search } from "../../components/Common/Table";
import { useNavigate, useLocation } from "react-router-dom";
import { IoIosArrowDown, IoIosArrowUp } from "react-icons/io";
import { BsEye } from "react-icons/bs";
import FilterSubmitform from "./FilterSubmitform";
import { submittedFormList } from "../../Service/submittedForm";
import moment from "moment";
import TableRowsLoader from "../../components/Common/Loader/Skeleton";
import { AuditorDropdown } from "../../Service/Auditor.service";
import { surveyFormDropdown, surveyFormList } from "../../Service/form.service";

import { submitformFilterInitialValues } from "../../helper/initialValues";
import { useFormik } from "formik";

const Submitform = () => {
  const [search, setSearch] = useState(() => {
    return sessionStorage.getItem("submitted_form_search") || "";
  });
  const [debouncedSearch, setDebouncedSearch] = useState(() => {
    return sessionStorage.getItem("submitted_form_search") || "";
  });
  const [page, setPage] = useState(() => {
    const saved = sessionStorage.getItem("submitted_form_page");
    return saved !== null && !isNaN(Number(saved)) ? Number(saved) : 0;
  });
  const [value, setValue] = useState([]);
  const [formData, setFormData] = useState([]);
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    const saved = sessionStorage.getItem("submitted_form_rows_per_page");
    return saved !== null && !isNaN(Number(saved)) ? Number(saved) : 10;
  });
  const [totalData, setTotalData] = useState(0);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [auditordata, setAuditorData] = useState([]);
  const [formList, setFormList] = useState([]);
  const [syncModalOpen, setSyncModalOpen] = useState(false);
  const [showSyncButton, setShowSyncButton] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const locationAuditorId = location.state?.auditorId || new URLSearchParams(location.search).get("auditorId");

  useEffect(() => {
    getGlobalSettings().then((res) => {
      if (res && res.status && res.data) {
        setShowSyncButton(Boolean(res.data.showMongoSyncButton));
      }
    }).catch(() => {});
  }, []);

  const getDefaultMonthFilters = () => ({
    fromSubmitDate: moment().startOf('month').format('YYYY-MM-DD'),
    toSubmitDate: moment().endOf('month').format('YYYY-MM-DD'),
    auditor: "",
    formId: "",
  });

  const [appliedFilters, setAppliedFilters] = useState(() => {
    if (locationAuditorId) {
      return {
        fromSubmitDate: "",
        toSubmitDate: "",
        auditor: locationAuditorId,
        formId: "",
      };
    }
    if (location.state?.resetToDefault) {
      return getDefaultMonthFilters();
    }
    const saved = sessionStorage.getItem("submitted_form_filters");
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (parsed && typeof parsed === "object") {
          return {
            fromSubmitDate: parsed.fromSubmitDate || moment().startOf('month').format('YYYY-MM-DD'),
            toSubmitDate: parsed.toSubmitDate || moment().endOf('month').format('YYYY-MM-DD'),
            auditor: parsed.auditor || "",
            formId: parsed.formId || "",
          };
        }
      } catch (e) {}
    }
    return getDefaultMonthFilters();
  });

  const [sortConfig, setSortConfig] = useState(() => {
    const saved = sessionStorage.getItem("submitted_form_sort");
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {}
    }
    return {
      key: "",
      direction: "",
    };
  });

  const toggleDrawer = (forceState) => {
    setOpenDrawer((prev) => (typeof forceState === "boolean" ? forceState : !prev));
  };

  useEffect(() => {
    if (location.state?.resetToDefault) {
      const def = getDefaultMonthFilters();
      setAppliedFilters(def);
      setSearch("");
      setDebouncedSearch("");
      setPage(0);
      sessionStorage.setItem("submitted_form_filters", JSON.stringify(def));
      sessionStorage.setItem("submitted_form_page", 0);
      sessionStorage.setItem("submitted_form_search", "");
    }
  }, [location.state?.resetToDefault]);

  useEffect(() => {
    if (locationAuditorId) {
      const targetFilter = {
        fromSubmitDate: "",
        toSubmitDate: "",
        auditor: locationAuditorId,
        formId: "",
      };
      setAppliedFilters(targetFilter);
      sessionStorage.setItem("submitted_form_filters", JSON.stringify(targetFilter));
      setPage(0);
      sessionStorage.setItem("submitted_form_page", 0);
    }
  }, [locationAuditorId]);

  useEffect(() => {
    sessionStorage.setItem("submitted_form_page", page);
  }, [page]);

  useEffect(() => {
    sessionStorage.setItem("submitted_form_rows_per_page", rowsPerPage);
  }, [rowsPerPage]);

  useEffect(() => {
    sessionStorage.setItem("submitted_form_search", debouncedSearch);
  }, [debouncedSearch]);

  useEffect(() => {
    sessionStorage.setItem("submitted_form_filters", JSON.stringify(appliedFilters));
  }, [appliedFilters]);

  useEffect(() => {
    sessionStorage.setItem("submitted_form_sort", JSON.stringify(sortConfig));
  }, [sortConfig]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
    }, 400);
    return () => clearTimeout(handler);
  }, [search]);

  // When search, appliedFilters, or sortConfig changes, reset page to 0
  const isFirstRun = useRef(true);
  useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false;
      return;
    }
    setPage(0);
    sessionStorage.setItem("submitted_form_page", 0);
  }, [debouncedSearch, appliedFilters, sortConfig]);

  const getSubmitFormList = useCallback(async (pageNum = page) => {
    setFormData([]);

    let selectQuery = `?page=${pageNum + 1}&limit=${rowsPerPage}&search=${encodeURIComponent(
      debouncedSearch || ""
    )}&sortBy=${sortConfig.key || ""}&sortFlag=${
      sortConfig.direction || ""
    }&fromSubmittedDate=${
      appliedFilters.fromSubmitDate || ""
    }&toSubmittedDate=${
      appliedFilters.toSubmitDate || ""
    }&auditorId=${
      appliedFilters.auditor && appliedFilters.auditor !== "SelectCustomer" ? appliedFilters.auditor : ""
    }&formId=${
      appliedFilters.formId && appliedFilters.formId !== "SelectCustomer" ? appliedFilters.formId : ""
    }`;

    try {
      const res = await submittedFormList(selectQuery);
      if (res?.status && res?.data && res?.data?.length > 0) {
        setFormData(res.data);
        setTotalData(res.total || 0);
      } else {
        setFormData(null);
        setTotalData(0);
      }
    } catch (err) {
      console.error(err);
      setFormData(null);
      setTotalData(0);
    }
  }, [debouncedSearch, rowsPerPage, sortConfig, appliedFilters, page]);

  useEffect(() => {
    getSubmitFormList(page);
  }, [page, debouncedSearch, appliedFilters, sortConfig]);

  const getDropdownData = async () => {
    try {
      const res = await AuditorDropdown();
      if (res?.status) {
        const data = res?.data?.map((item) => ({
          id: item._id,
          name: item.firstName + " " + item.lastName,
        }));
        setAuditorData(data);
      } else {
        setAuditorData([]);
      }
    } catch (err) {
      setAuditorData([]);
    }

    try {
      let formRes = await surveyFormDropdown();
      if (formRes?.status && formRes?.data && formRes?.data?.length > 0) {
        const data = formRes.data.map((item) => ({
          id: item._id,
          name: item.title,
        }));
        setFormList(data);
      } else {
        let fallbackRes = await surveyFormList('?limit=500&status=1');
        if (fallbackRes?.status && fallbackRes?.data && fallbackRes?.data?.length > 0) {
          const data = fallbackRes.data.map((item) => ({
            id: item._id,
            name: item.title,
          }));
          setFormList(data);
        } else {
          setFormList([]);
        }
      }
    } catch (err) {
      console.error(err);
      setFormList([]);
    }
  };

  useEffect(() => {
    getDropdownData();
  }, []);

  const formik1 = useFormik({
    initialValues: {
      fromSubmitDate: appliedFilters.fromSubmitDate || "",
      toSubmitDate: appliedFilters.toSubmitDate || "",
      auditor: appliedFilters.auditor || "",
      formId: appliedFilters.formId || "",
    },
    enableReinitialize: true,
    onSubmit: async (values) => {
      setOpenDrawer(false);
      setAppliedFilters({
        fromSubmitDate: values.fromSubmitDate || "",
        toSubmitDate: values.toSubmitDate || "",
        auditor: values.auditor === "SelectCustomer" ? "" : (values.auditor || ""),
        formId: values.formId === "SelectCustomer" ? "" : (values.formId || ""),
      });
    },
  });

  const handleClearFilter = () => {
    const defaultFilters = getDefaultMonthFilters();
    formik1.resetForm({
      values: defaultFilters,
    });
    setAppliedFilters(defaultFilters);
    sessionStorage.setItem("submitted_form_filters", JSON.stringify(defaultFilters));
    sessionStorage.setItem("submitted_form_page", 0);
    setPage(0);
  };

  const hasActiveFilter = Boolean(
    appliedFilters.fromSubmitDate ||
    appliedFilters.toSubmitDate ||
    appliedFilters.auditor ||
    appliedFilters.formId
  );

  return (
    <>
      <Heading head={"Submited Forms"} />

      <Stack gap={2} mt={1}>
        <Stack
          mb={1}
          flexDirection={"row"}
          justifyContent={"space-between"}
          alignItems={"center"}
        >
          <Search search={search} setSearch={setSearch} setPage={setPage} />
          <Stack
            flexDirection={"row"}
            gap={1}
            alignItems={"center"}
            justifyContent={"flex-end"}
          >
            {showSyncButton && (
              <MuiButton
                variant="outlined"
                size="small"
                onClick={() => setSyncModalOpen(true)}
                startIcon={<IoSyncOutline style={{ fontSize: "16px" }} />}
                sx={{
                  mt: 2,
                  borderColor: "#DA2921",
                  color: "#DA2921",
                  fontWeight: 600,
                  textTransform: "none",
                  fontSize: "13px",
                  height: "38px",
                  borderRadius: "6px",
                  px: 1.5,
                  "&:hover": {
                    borderColor: "#b71c1c",
                    backgroundColor: "#DA292110",
                  },
                }}
              >
                Sync Data
              </MuiButton>
            )}
            <Badge
              color="error"
              variant="dot"
              invisible={!hasActiveFilter}
              sx={{ mt: 2 }}
            >
              <Button
                buttonName={<VscFilter style={{ color: "#000000", fontSize: "18px" }} />}
                style={{
                  padding: "10px 11px",
                  color: "#000000",
                }}
                color="white"
                size="small"
                onClick={toggleDrawer}
                variant="contained"
              />
            </Badge>
          </Stack>
          <FilterSubmitform
            value={value}
            setValue={setValue}
            openDrawer={openDrawer}
            toggleDrawer={toggleDrawer}
            formik={formik1}
            auditordata={auditordata}
            formdata={formList}
            onClear={handleClearFilter}
          />
        </Stack>

        {hasActiveFilter && (
          <Stack
            flexDirection="row"
            gap={1}
            alignItems="center"
            flexWrap="wrap"
            sx={{
              backgroundColor: "#f8f9fa",
              p: 1.2,
              borderRadius: "8px",
              border: "1px solid #e9ecef",
            }}
          >
            <Typography fontSize="13px" fontWeight={600} color="text.secondary">
              Active Filters:
            </Typography>

            {(appliedFilters.fromSubmitDate || appliedFilters.toSubmitDate) && (
              <Chip
                size="small"
                color="primary"
                variant="outlined"
                label={`📅 ${
                  appliedFilters.fromSubmitDate
                    ? moment(appliedFilters.fromSubmitDate).format("DD-MM-YYYY")
                    : "Any"
                } to ${
                  appliedFilters.toSubmitDate
                    ? moment(appliedFilters.toSubmitDate).format("DD-MM-YYYY")
                    : "Any"
                }`}
                onDelete={() => {
                  formik1.setFieldValue("fromSubmitDate", "");
                  formik1.setFieldValue("toSubmitDate", "");
                  setAppliedFilters((prev) => ({
                    ...prev,
                    fromSubmitDate: "",
                    toSubmitDate: "",
                  }));
                }}
              />
            )}

            {appliedFilters.formId && (
              <Chip
                size="small"
                color="primary"
                variant="outlined"
                label={`📋 Form: ${
                  formList.find((f) => (f.id || f._id) === appliedFilters.formId)?.name ||
                  "Selected Form"
                }`}
                onDelete={() => {
                  formik1.setFieldValue("formId", "");
                  setAppliedFilters((prev) => ({
                    ...prev,
                    formId: "",
                  }));
                }}
              />
            )}

            {appliedFilters.auditor && (
              <Chip
                size="small"
                color="primary"
                variant="outlined"
                label={`👤 Auditor: ${
                  auditordata.find((a) => (a.id || a._id) === appliedFilters.auditor)?.name ||
                  (location.state?.auditorId === appliedFilters.auditor ? location.state?.auditorName : null) ||
                  "Selected Auditor"
                }`}
                onDelete={() => {
                  formik1.setFieldValue("auditor", "");
                  setAppliedFilters((prev) => ({
                    ...prev,
                    auditor: "",
                  }));
                }}
              />
            )}

            <Button
              buttonName="Clear All"
              size="small"
              color="white"
              style={{
                padding: "2px 8px",
                fontSize: "11px",
                marginLeft: "auto",
                border: "1px solid #ccc",
              }}
              onClick={handleClearFilter}
            />
          </Stack>
        )}
        <TableContainer component={Paper}>
          <Table stickyHeader aria-label="simple table">
            <TableHead>
              <TableRow>
                <TableCell style={{ cursor: "pointer" }}>
                  <Stack
                    flexDirection={"row"}
                    gap={2}
                    alignItems={"center"}
                    justifyContent={"start"}
                  >
                    <div>Title</div>
                  </Stack>
                </TableCell>
                <TableCell style={{ cursor: "pointer" }}>
                  <Stack
                    flexDirection={"row"}
                    gap={2}
                    alignItems={"center"}
                    justifyContent={"start"}
                  >
                    <div>Auditor Name</div>
                  </Stack>
                </TableCell>
                <TableCell style={{ cursor: "pointer" }}>
                  <Stack
                    flexDirection={"row"}
                    gap={2}
                    alignItems={"center"}
                    justifyContent={"start"}
                  >
                    <div>Device Name</div>
                  </Stack>
                </TableCell>
                <TableCell style={{ cursor: "pointer" }}>
                  <Stack
                    flexDirection={"row"}
                    gap={2}
                    alignItems={"center"}
                    justifyContent={"start"}
                  >
                    <div>Device Id</div>
                  </Stack>
                </TableCell>

                <TableCell
                  style={{ cursor: "pointer" }}
                  onClick={() =>
                    setSortConfig({
                      key: "submittedDate",
                      direction:
                        sortConfig &&
                        sortConfig.key === "submittedDate" &&
                        sortConfig.direction === "asc"
                          ? "desc"
                          : "asc",
                    })
                  }
                >
                  <Stack
                    flexDirection={"row"}
                    gap={2}
                    alignItems={"center"}
                    justifyContent={"start"}
                  >
                    <div>{"Submited-Date"}</div>
                    {sortConfig &&
                    sortConfig.key === "submittedDate" &&
                    sortConfig.direction === "asc" ? (
                      <IoIosArrowUp className="iconstyle" />
                    ) : (
                      <IoIosArrowDown />
                    )}
                  </Stack>
                </TableCell>

                <TableCell
                  style={{
                    cursor: "pointer",
                    textAlign: "right",
                  }}
                >
                  <Stack
                    flexDirection={"row"}
                    gap={2}
                    alignItems={"center"}
                    justifyContent={"start"}
                  >
                    <div>{"Action"}</div>
                  </Stack>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {formData &&
                formData?.length > 0 &&
                formData?.map((data) => (
                  <TableRow
                    key={data._id || data.id}
                    sx={{
                      "&:last-child td, &:last-child th": { border: 0 },
                      cursor: "pointer",
                    }}
                    onClick={() => {
                      navigate(`/form-view`, { state: { id: data._id } });
                    }}
                  >
                    <TableCell
                      className="table-hover"
                      style={{ textAlign: "left" }}
                    >
                      {data?.fromId?.title}
                    </TableCell>
                    <TableCell style={{ textAlign: "left" }}>
                      {data.auditorId
                        ? `${data.auditorId.firstName || ""} ${data.auditorId.lastName || ""}`.trim()
                        : "-"}
                    </TableCell>
                    <TableCell style={{ textAlign: "left" }}>
                      {data?.deviceName || "-"}
                    </TableCell>
                    <TableCell style={{ textAlign: "left" }}>
                      {data?.deviceId || "-"}
                    </TableCell>
                    <TableCell style={{ textAlign: "left" }}>
                      {data?.submittedDate
                        ? moment(data?.submittedDate).format("DD-MM-YYYY hh:mm A")
                        : "-"}
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Stack
                        flexDirection={"row"}
                        gap={2}
                        alignItems={"center"}
                        justifyContent={"start"}
                        onClick={() => {
                          navigate(`/form-view`, { state: { id: data._id } });
                        }}
                      >
                        <BsEye style={{ fontSize: "20px" }} />
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              {formData == null ? (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    align="center"
                    sx={{
                      textAlign: "center !important",
                      py: 8,
                      borderBottom: "none",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "100%",
                        margin: "0 auto",
                        textAlign: "center",
                      }}
                    >
                      <Typography
                        variant="h6"
                        color="text.secondary"
                        fontWeight={500}
                        gutterBottom
                        sx={{ textAlign: "center", width: "100%" }}
                      >
                        No records to display
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        mb={2}
                        sx={{ textAlign: "center", width: "100%" }}
                      >
                        No submissions match the selected date range or filters.
                      </Typography>
                      {hasActiveFilter && (
                        <Button
                          buttonName="Clear All Filters"
                          size="small"
                          color="white"
                          style={{ border: "1px solid #ccc", padding: "6px 16px" }}
                          onClick={handleClearFilter}
                        />
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ) : formData && formData.length === 0 ? (
                <TableRowsLoader colNumber={6} rowsNum={10} />
              ) : (
                ""
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <Pagination
            count={10}
            shape="rounded"
            totalData={totalData}
            page={page}
            setPage={setPage}
            rowsPerPage={rowsPerPage}
            setRowsPerPage={setRowsPerPage}
          />
        </div>
        {showSyncButton && (
          <MongoSyncModal
            open={syncModalOpen}
            onClose={() => setSyncModalOpen(false)}
            onSyncComplete={() => {
              getSubmitFormList();
            }}
          />
        )}
      </Stack>
    </>
  );
};

export default Submitform;
